import { useState } from 'react';
import { X, ListMusic, Trash2, GripVertical, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueue } from '../contexts/QueueContext';

const QueueDrawer = ({ onPlayTrack }) => {
  const [open, setOpen] = useState(false);
  const { queue, currentIndex, removeFromQueue, clearQueue, setPlayingIndex } = useQueue();

  const handlePlayFromQueue = (index) => {
    setPlayingIndex(index);
    const track = queue[index];
    if (track && onPlayTrack) onPlayTrack(track);
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="queue-toggle-btn"
        className={`relative p-2 rounded-sm transition-colors ${open ? 'text-primary' : 'hover:text-primary text-muted-foreground'}`}
        title="Play queue"
      >
        <ListMusic className="w-5 h-5" />
        {queue.length > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] font-bold bg-primary text-black rounded-full flex items-center justify-center">
            {queue.length > 9 ? '9+' : queue.length}
          </span>
        )}
      </button>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-full right-0 mb-2 w-80 max-h-[400px] bg-zinc-900 border border-white/10 rounded-sm shadow-2xl overflow-hidden z-50"
            data-testid="queue-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="font-bold text-sm flex items-center gap-2">
                <ListMusic className="w-4 h-4 text-primary" />
                Queue ({queue.length})
              </h3>
              <div className="flex items-center gap-2">
                {queue.length > 0 && (
                  <button
                    onClick={clearQueue}
                    data-testid="clear-queue-btn"
                    className="text-xs text-muted-foreground hover:text-red-400 transition-colors"
                  >
                    Clear all
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/5 rounded-sm">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Queue List */}
            <div className="overflow-y-auto max-h-[320px]">
              {queue.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <ListMusic className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Queue is empty</p>
                  <p className="text-xs mt-1">Add tracks from mixtapes or releases</p>
                </div>
              ) : (
                queue.map((track, idx) => (
                  <div
                    key={track.track_id + idx}
                    data-testid={`queue-item-${idx}`}
                    className={`flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors group ${
                      idx === currentIndex ? 'bg-primary/10 border-l-2 border-primary' : ''
                    }`}
                  >
                    <GripVertical className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
                    <button
                      onClick={() => handlePlayFromQueue(idx)}
                      className="flex-shrink-0"
                    >
                      {track.cover ? (
                        <img src={track.cover} alt="" className="w-10 h-10 rounded-sm object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center">
                          <Play className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() => handlePlayFromQueue(idx)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className={`text-xs font-medium truncate ${idx === currentIndex ? 'text-primary' : ''}`}>
                        {track.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">{track.artist}</p>
                    </button>
                    <button
                      onClick={() => removeFromQueue(track.track_id)}
                      data-testid={`remove-queue-${idx}`}
                      className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QueueDrawer;
